import React, { useState, useEffect, useCallback, useRef } from "react";
import AdminNavbar from "../components/AdminNavbar";
import { api } from "../context/AppContext";

/* ─── action config ─── */
const ACTIONS = {
  login: {
    label: "Login",
    color: "#3b82f6",
    bg: "rgba(59,130,246,0.1)",
    icon: "🔑",
  },
  signup: {
    label: "Sign Up",
    color: "#8b5cf6",
    bg: "rgba(139,92,246,0.1)",
    icon: "✨",
  },
  otp_sent: {
    label: "OTP Sent",
    color: "#6366f1",
    bg: "rgba(99,102,241,0.1)",
    icon: "📱",
  },
  otp_verified: {
    label: "OTP Verified",
    color: "#10b981",
    bg: "rgba(16,185,129,0.1)",
    icon: "✅",
  },
  otp_failed: {
    label: "OTP Failed",
    color: "#ef4444",
    bg: "rgba(239,68,68,0.1)",
    icon: "❌",
  },
  profile_updated: {
    label: "Profile Updated",
    color: "#14b8a6",
    bg: "rgba(20,184,166,0.1)",
    icon: "👤",
  },
  role_changed: {
    label: "Role Changed",
    color: "#f59e0b",
    bg: "rgba(245,158,11,0.1)",
    icon: "🛡",
  },
  user_deleted: {
    label: "User Deleted",
    color: "#ef4444",
    bg: "rgba(239,68,68,0.1)",
    icon: "🗑",
  },
  order_created: {
    label: "Order Created",
    color: "#22c55e",
    bg: "rgba(34,197,94,0.1)",
    icon: "🛒",
  },
  order_status: {
    label: "Order Status",
    color: "#f97316",
    bg: "rgba(249,115,22,0.1)",
    icon: "📦",
  },
  product_added: {
    label: "Product Added",
    color: "#22c55e",
    bg: "rgba(34,197,94,0.1)",
    icon: "➕",
  },
  product_updated: {
    label: "Product Updated",
    color: "#f59e0b",
    bg: "rgba(245,158,11,0.1)",
    icon: "✏️",
  },
  product_deleted: {
    label: "Product Deleted",
    color: "#ef4444",
    bg: "rgba(239,68,68,0.1)",
    icon: "🗑",
  },
  product_toggled: {
    label: "Product Toggled",
    color: "#6366f1",
    bg: "rgba(99,102,241,0.1)",
    icon: "🔄",
  },
  viewed_orders: {
    label: "Viewed Orders",
    color: "#94a3b8",
    bg: "rgba(148,163,184,0.08)",
    icon: "👁",
  },
  viewed_payments: {
    label: "Viewed Payments",
    color: "#94a3b8",
    bg: "rgba(148,163,184,0.08)",
    icon: "👁",
  },
};

const STATUS_COLORS = {
  pending: "#f59e0b",
  confirmed: "#3b82f6",
  preparing: "#8b5cf6",
  shipped: "#06b6d4",
  out_for_delivery: "#f97316",
  delivered: "#22c55e",
  cancelled: "#ef4444",
};

const ENTITIES = ["All", "Order", "Product", "User", "Payment", "Auth"];
const ROLES = ["All", "admin", "user"];
const PER_PAGE = 20;

/* ─── tiny components ─── */
const ActionBadge = ({ action }) => {
  const cfg = ACTIONS[action] || {
    label: action,
    color: "#64748b",
    bg: "rgba(100,116,139,0.1)",
    icon: "•",
  };
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: "3px 9px",
        borderRadius: 6,
        background: cfg.bg,
        color: cfg.color,
        fontSize: 11,
        fontWeight: 700,
        border: `1px solid ${cfg.color}30`,
        whiteSpace: "nowrap",
      }}
    >
      <span style={{ fontSize: 11 }}>{cfg.icon}</span>
      {cfg.label}
    </span>
  );
};

const StatusPill = ({ status }) => {
  const c = STATUS_COLORS[status] || "#94a3b8";
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 3,
        padding: "2px 7px",
        borderRadius: 5,
        background: `${c}18`,
        color: c,
        fontSize: 11,
        fontWeight: 700,
        border: `1px solid ${c}35`,
      }}
    >
      <span
        style={{
          width: 5,
          height: 5,
          borderRadius: "50%",
          background: c,
          display: "inline-block",
        }}
      />
      {status}
    </span>
  );
};

/* dropdown helper */
function FilterDropdown({
  label,
  value,
  options,
  onChange,
  renderOption,
  width = 180,
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const h = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const displayLabel = renderOption ? renderOption(value, true) : value;

  return (
    <div style={{ position: "relative" }} ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 7,
          padding: "8px 13px",
          borderRadius: 10,
          background: value !== options[0] ? "rgba(45,139,78,0.06)" : "#fff",
          border: `1.5px solid ${value !== options[0] ? "#2D8B4E" : "#e5e7eb"}`,
          color: value !== options[0] ? "#2D8B4E" : "#374151",
          fontSize: 13,
          fontWeight: value !== options[0] ? 700 : 600,
          cursor: "pointer",
          whiteSpace: "nowrap",
          transition: "all 0.15s",
        }}
        onMouseEnter={(e) => {
          if (value === options[0]) {
            e.currentTarget.style.borderColor = "#2D8B4E";
          }
        }}
        onMouseLeave={(e) => {
          if (value === options[0]) {
            e.currentTarget.style.borderColor = "#e5e7eb";
          }
        }}
      >
        {displayLabel}
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          style={{
            transition: "transform 0.2s",
            transform: open ? "rotate(180deg)" : "none",
            flexShrink: 0,
          }}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 5px)",
            left: 0,
            zIndex: 100,
            width: width,
            background: "#fff",
            borderRadius: 12,
            border: "1px solid #e5e7eb",
            boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
            overflow: "hidden",
            animation: "dropIn 0.15s ease",
          }}
        >
          <div style={{ maxHeight: 280, overflowY: "auto" }}>
            {options.map((opt) => {
              const isActive = opt === value;
              return (
                <button
                  key={opt}
                  onClick={() => {
                    onChange(opt);
                    setOpen(false);
                  }}
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "9px 14px",
                    border: "none",
                    textAlign: "left",
                    background: isActive
                      ? "rgba(45,139,78,0.07)"
                      : "transparent",
                    color: isActive ? "#2D8B4E" : "#374151",
                    fontSize: 13,
                    fontWeight: isActive ? 700 : 500,
                    cursor: "pointer",
                    transition: "background 0.1s",
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive) e.currentTarget.style.background = "#f9fafb";
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive)
                      e.currentTarget.style.background = "transparent";
                  }}
                >
                  {isActive && (
                    <span style={{ color: "#2D8B4E", fontSize: 11 }}>✓</span>
                  )}
                  {renderOption ? renderOption(opt, false) : opt}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── main component ─── */
export default function AdminAuditLogs() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expandedId, setExpandedId] = useState(null);

  /* filters */
  const [actionFilter, setActionFilter] = useState("All");
  const [entityFilter, setEntityFilter] = useState("All");
  const [roleFilter, setRoleFilter] = useState("All");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(1);

  /* search debounce */
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 380);
    return () => clearTimeout(t);
  }, [searchInput]);

  /* fetch */
  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const p = new URLSearchParams({ page, limit: PER_PAGE });
      if (actionFilter !== "All") p.append("action", actionFilter);
      if (entityFilter !== "All") p.append("entity", entityFilter);
      if (roleFilter !== "All") p.append("role", roleFilter);
      if (search.trim()) p.append("search", search.trim());

      const res = await api.get(`/admin/audit-logs?${p}`);
      const data = res.data;
      setLogs(Array.isArray(data) ? data : data?.logs || []);
      setTotal(data?.total || 0);
    } catch {
      setError("Failed to load audit logs.");
      setLogs(DEMO_LOGS);
      setTotal(DEMO_LOGS.length);
    } finally {
      setLoading(false);
    }
  }, [actionFilter, entityFilter, roleFilter, search, page]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  /* reset page when filters change */
  useEffect(() => {
    setPage(1);
  }, [actionFilter, entityFilter, roleFilter]);

  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));
  const hasActiveFilter =
    actionFilter !== "All" ||
    entityFilter !== "All" ||
    roleFilter !== "All" ||
    search;

  const resetFilters = () => {
    setActionFilter("All");
    setEntityFilter("All");
    setRoleFilter("All");
    setSearchInput("");
    setSearch("");
    setPage(1);
  };

  const formatDate = (d) => {
    if (!d) return "";
    const dt = new Date(d);
    return (
      dt.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "2-digit",
      }) +
      " · " +
      dt.toLocaleTimeString("en-IN", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
    );
  };

  const formatDateGroup = (d) => {
    if (!d) return "";
    const dt = new Date(d);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const yest = new Date(today);
    yest.setDate(yest.getDate() - 1);
    const dDate = new Date(dt);
    dDate.setHours(0, 0, 0, 0);
    if (dDate.getTime() === today.getTime()) return "Today";
    if (dDate.getTime() === yest.getTime()) return "Yesterday";
    return dt.toLocaleDateString("en-IN", {
      weekday: "long",
      day: "2-digit",
      month: "long",
    });
  };

  /* group logs by day */
  const grouped = logs.reduce((acc, log) => {
    const key = formatDateGroup(log.createdAt || log.timestamp);
    if (!acc[key]) acc[key] = [];
    acc[key].push(log);
    return acc;
  }, {});

  const actionOptions = ["All", ...Object.keys(ACTIONS)];

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#f7f9fb",
        fontFamily: "'Roboto', sans-serif",
      }}
    >
      <AdminNavbar />

      {/* ── Page Header ── */}
      <div
        style={{
          background: "#fff",
          borderBottom: "1px solid #f0f0f0",
          padding: "20px 28px",
        }}
      >
        <div
          style={{
            maxWidth: 1300,
            margin: "0 auto",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 12,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 12,
                background:
                  "linear-gradient(135deg, rgba(45,139,78,0.12), rgba(76,175,114,0.1))",
                border: "1px solid rgba(45,139,78,0.15)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 20,
              }}
            >
              📋
            </div>
            <div>
              <h1
                style={{
                  fontSize: 20,
                  fontWeight: 900,
                  color: "#0f172a",
                  margin: 0,
                  letterSpacing: "-0.3px",
                }}
              >
                Audit Logs
              </h1>
              <p
                style={{
                  fontSize: 12,
                  color: "#94a3b8",
                  margin: 0,
                  marginTop: 2,
                }}
              >
                Complete trail of all system events
                {total > 0 ? ` · ${total.toLocaleString()} total records` : ""}
              </p>
            </div>
          </div>

          <button
            onClick={fetchLogs}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "9px 18px",
              borderRadius: 10,
              border: "none",
              cursor: "pointer",
              background: "linear-gradient(135deg, #2D8B4E, #4CAF72)",
              color: "#fff",
              fontSize: 13,
              fontWeight: 700,
              boxShadow: "0 4px 14px rgba(45,139,78,0.28)",
              transition: "all 0.2s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-1px)";
              e.currentTarget.style.boxShadow =
                "0 6px 20px rgba(45,139,78,0.38)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "none";
              e.currentTarget.style.boxShadow =
                "0 4px 14px rgba(45,139,78,0.28)";
            }}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="23 4 23 10 17 10" />
              <polyline points="1 20 1 14 7 14" />
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
            </svg>
            Refresh
          </button>
        </div>
      </div>

      {/* ── Filters Bar ── */}
      <div
        style={{
          background: "#fff",
          borderBottom: "1px solid #f0f0f0",
          padding: "12px 28px",
        }}
      >
        <div
          style={{
            maxWidth: 1300,
            margin: "0 auto",
            display: "flex",
            alignItems: "center",
            gap: 10,
            flexWrap: "wrap",
          }}
        >
          {/* search */}
          <div
            style={{ position: "relative", flex: "1 1 200px", maxWidth: 280 }}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#9ca3af"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{
                position: "absolute",
                left: 11,
                top: "50%",
                transform: "translateY(-50%)",
                pointerEvents: "none",
              }}
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="text"
              placeholder="Search phone, entity ID…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              style={{
                width: "100%",
                padding: "8px 34px 8px 34px",
                borderRadius: 10,
                border: "1.5px solid #e5e7eb",
                outline: "none",
                fontSize: 13,
                color: "#374151",
                background: "#fafafa",
                transition: "border-color 0.15s",
                boxSizing: "border-box",
              }}
              onFocus={(e) => (e.target.style.borderColor = "#2D8B4E")}
              onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
            />
            {searchInput && (
              <button
                onClick={() => setSearchInput("")}
                style={{
                  position: "absolute",
                  right: 10,
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: "#9ca3af",
                  fontSize: 14,
                  lineHeight: 1,
                }}
              >
                ✕
              </button>
            )}
          </div>

          <div
            style={{
              width: 1,
              height: 28,
              background: "#e5e7eb",
              flexShrink: 0,
            }}
          />

          {/* action filter */}
          <FilterDropdown
            label="Action"
            value={actionFilter}
            options={actionOptions}
            onChange={(v) => setActionFilter(v)}
            width={210}
            renderOption={(opt, isDisplay) => {
              if (opt === "All")
                return isDisplay ? "All Actions" : "All Actions";
              const cfg = ACTIONS[opt];
              if (!cfg) return opt;
              return (
                <span style={{ display: "flex", alignItems: "center", gap: 7 }}>
                  <span style={{ fontSize: 13 }}>{cfg.icon}</span>
                  {isDisplay ? cfg.label : cfg.label}
                </span>
              );
            }}
          />

          {/* entity filter */}
          <FilterDropdown
            label="Entity"
            value={entityFilter}
            options={ENTITIES}
            onChange={(v) => setEntityFilter(v)}
            width={150}
            renderOption={(opt) => (opt === "All" ? "All Entities" : opt)}
          />

          {/* role filter */}
          <FilterDropdown
            label="Role"
            value={roleFilter}
            options={ROLES}
            onChange={(v) => setRoleFilter(v)}
            width={130}
            renderOption={(opt) =>
              opt === "All"
                ? "All Roles"
                : opt.charAt(0).toUpperCase() + opt.slice(1)
            }
          />

          {/* clear */}
          {hasActiveFilter && (
            <button
              onClick={resetFilters}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 5,
                padding: "8px 13px",
                borderRadius: 10,
                background: "rgba(239,68,68,0.07)",
                border: "1.5px solid rgba(239,68,68,0.25)",
                color: "#ef4444",
                fontSize: 13,
                fontWeight: 700,
                cursor: "pointer",
                transition: "all 0.15s",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.background = "rgba(239,68,68,0.12)")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.background = "rgba(239,68,68,0.07)")
              }
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
              Clear filters
            </button>
          )}

          <span
            style={{
              marginLeft: "auto",
              fontSize: 12,
              color: "#9ca3af",
              fontWeight: 500,
              whiteSpace: "nowrap",
            }}
          >
            {total > 0
              ? `${Math.min((page - 1) * PER_PAGE + 1, total)}–${Math.min(page * PER_PAGE, total)} of ${total.toLocaleString()}`
              : "0 records"}
          </span>
        </div>
      </div>

      {/* ── Logs ── */}
      <div
        style={{ maxWidth: 1300, margin: "0 auto", padding: "20px 28px 40px" }}
      >
        {loading && (
          <div
            style={{
              textAlign: "center",
              padding: "80px 0",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 12,
            }}
          >
            <div
              style={{
                width: 36,
                height: 36,
                border: "3px solid #e5e7eb",
                borderTopColor: "#2D8B4E",
                borderRadius: "50%",
                animation: "spin 0.7s linear infinite",
              }}
            />
            <p style={{ color: "#94a3b8", fontSize: 14, fontWeight: 500 }}>
              Loading audit logs…
            </p>
          </div>
        )}

        {!loading && error && (
          <div style={{ textAlign: "center", padding: "60px 0" }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>⚠️</div>
            <p style={{ fontWeight: 700, color: "#374151", marginBottom: 6 }}>
              {error}
            </p>
            <button
              onClick={fetchLogs}
              style={{
                padding: "9px 20px",
                borderRadius: 10,
                border: "none",
                cursor: "pointer",
                background: "linear-gradient(135deg,#2D8B4E,#4CAF72)",
                color: "#fff",
                fontWeight: 700,
              }}
            >
              Retry
            </button>
          </div>
        )}

        {!loading && !error && logs.length === 0 && (
          <div style={{ textAlign: "center", padding: "80px 0" }}>
            <div style={{ fontSize: 48, marginBottom: 14 }}>📋</div>
            <p
              style={{
                fontWeight: 700,
                color: "#374151",
                fontSize: 16,
                marginBottom: 6,
              }}
            >
              No logs found
            </p>
            <p style={{ color: "#94a3b8", fontSize: 13, marginBottom: 20 }}>
              Try adjusting your filters
            </p>
            {hasActiveFilter && (
              <button
                onClick={resetFilters}
                style={{
                  padding: "9px 20px",
                  borderRadius: 10,
                  border: "none",
                  cursor: "pointer",
                  background: "linear-gradient(135deg,#2D8B4E,#4CAF72)",
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: 13,
                }}
              >
                Clear Filters
              </button>
            )}
          </div>
        )}

        {!loading && logs.length > 0 && (
          <>
            {/* grouped timeline */}
            {Object.entries(grouped).map(([dateGroup, groupLogs]) => (
              <div key={dateGroup} style={{ marginBottom: 24 }}>
                {/* date group header */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    marginBottom: 10,
                  }}
                >
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 800,
                      color: "#64748b",
                      letterSpacing: "0.8px",
                      textTransform: "uppercase",
                    }}
                  >
                    {dateGroup}
                  </span>
                  <div style={{ flex: 1, height: 1, background: "#e5e7eb" }} />
                  <span
                    style={{ fontSize: 11, color: "#94a3b8", fontWeight: 600 }}
                  >
                    {groupLogs.length} event{groupLogs.length !== 1 ? "s" : ""}
                  </span>
                </div>

                {/* log entries */}
                <div
                  style={{
                    background: "#fff",
                    borderRadius: 14,
                    border: "1px solid #f0f0f0",
                    overflow: "hidden",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                  }}
                >
                  {groupLogs.map((log, i) => {
                    const isExpanded = expandedId === (log._id || i);
                    const isLast = i === groupLogs.length - 1;
                    const cfg = ACTIONS[log.action] || {
                      label: log.action,
                      color: "#64748b",
                      bg: "rgba(100,116,139,0.08)",
                      icon: "•",
                    };
                    const phone = log.userPhone || log.phone || "—";
                    const isAdmin = log.userRole === "admin";

                    return (
                      <div
                        key={log._id || i}
                        style={{
                          borderBottom: isLast ? "none" : "1px solid #f8f8f8",
                        }}
                      >
                        {/* main row */}
                        <div
                          onClick={() =>
                            setExpandedId(isExpanded ? null : log._id || i)
                          }
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 12,
                            padding: "12px 18px",
                            cursor: "pointer",
                            transition: "background 0.12s",
                          }}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.background = "#fafbff")
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.background = "transparent")
                          }
                        >
                          {/* action color dot */}
                          <div
                            style={{
                              width: 8,
                              height: 8,
                              borderRadius: "50%",
                              background: cfg.color,
                              flexShrink: 0,
                              boxShadow: `0 0 0 3px ${cfg.color}22`,
                            }}
                          />

                          {/* avatar */}
                          <div
                            style={{
                              width: 32,
                              height: 32,
                              borderRadius: "50%",
                              flexShrink: 0,
                              background: isAdmin
                                ? "linear-gradient(135deg, #d97706, #f59e0b)"
                                : "linear-gradient(135deg, #2D8B4E, #4CAF72)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "#fff",
                              fontSize: 12,
                              fontWeight: 900,
                              boxShadow: `0 0 0 2px ${isAdmin ? "rgba(245,158,11,0.25)" : "rgba(45,139,78,0.2)"}`,
                            }}
                          >
                            {phone[0] || "?"}
                          </div>

                          {/* action badge */}
                          <div style={{ flexShrink: 0, minWidth: 140 }}>
                            <ActionBadge action={log.action} />
                          </div>

                          {/* phone + role */}
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                              flexShrink: 0,
                              minWidth: 160,
                            }}
                          >
                            <span
                              style={{
                                fontSize: 12.5,
                                fontWeight: 600,
                                color: "#374151",
                                fontFamily: "monospace",
                              }}
                            >
                              {phone}
                            </span>
                            <span
                              style={{
                                fontSize: 9,
                                fontWeight: 800,
                                padding: "2px 6px",
                                borderRadius: 5,
                                background: isAdmin
                                  ? "rgba(245,158,11,0.12)"
                                  : "rgba(59,130,246,0.1)",
                                color: isAdmin ? "#d97706" : "#3b82f6",
                                border: `1px solid ${isAdmin ? "rgba(245,158,11,0.28)" : "rgba(59,130,246,0.22)"}`,
                                textTransform: "uppercase",
                                letterSpacing: "0.4px",
                              }}
                            >
                              {log.userRole || "—"}
                            </span>
                          </div>

                          {/* entity + details */}
                          <div
                            style={{
                              flex: 1,
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                              minWidth: 0,
                              overflow: "hidden",
                            }}
                          >
                            {log.entityType && (
                              <span
                                style={{
                                  fontSize: 11.5,
                                  color: "#64748b",
                                  fontWeight: 600,
                                  flexShrink: 0,
                                }}
                              >
                                {log.entityType}
                              </span>
                            )}
                            {log.entityId && (
                              <span
                                style={{
                                  fontSize: 10.5,
                                  color: "#94a3b8",
                                  fontFamily: "monospace",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  maxWidth: 100,
                                }}
                              >
                                #{log.entityId.slice(-8)}
                              </span>
                            )}

                            {/* order status transition */}
                            {log.details?.from && log.details?.to && (
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 5,
                                  flexShrink: 0,
                                }}
                              >
                                <StatusPill status={log.details.from} />
                                <svg
                                  width="10"
                                  height="10"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="#94a3b8"
                                  strokeWidth="2.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                >
                                  <line x1="5" y1="12" x2="19" y2="12" />
                                  <polyline points="12 5 19 12 12 19" />
                                </svg>
                                <StatusPill status={log.details.to} />
                              </div>
                            )}

                            {/* role change */}
                            {log.details?.newRole && !log.details?.from && (
                              <span
                                style={{
                                  fontSize: 11,
                                  color: "#f59e0b",
                                  fontWeight: 700,
                                }}
                              >
                                → {log.details.newRole}
                              </span>
                            )}

                            {/* product name */}
                            {log.details?.name && (
                              <span
                                style={{
                                  fontSize: 11.5,
                                  color: "#374151",
                                  fontWeight: 600,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  maxWidth: 160,
                                }}
                              >
                                "{log.details.name}"
                              </span>
                            )}

                            {/* IP address */}
                            {log.ip && (
                              <span
                                style={{
                                  fontSize: 10,
                                  color: "#94a3b8",
                                  fontFamily: "monospace",
                                  background: "#f8fafc",
                                  padding: "1px 5px",
                                  borderRadius: 4,
                                  border: "1px solid #f0f0f0",
                                  flexShrink: 0,
                                }}
                              >
                                {log.ip}
                              </span>
                            )}
                          </div>

                          {/* time */}
                          <span
                            style={{
                              fontSize: 11.5,
                              color: "#94a3b8",
                              flexShrink: 0,
                              whiteSpace: "nowrap",
                              fontVariantNumeric: "tabular-nums",
                            }}
                          >
                            {formatDate(log.createdAt || log.timestamp)}
                          </span>

                          {/* expand chevron */}
                          <svg
                            width="13"
                            height="13"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#cbd5e1"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            style={{
                              flexShrink: 0,
                              transition: "transform 0.2s",
                              transform: isExpanded ? "rotate(180deg)" : "none",
                            }}
                          >
                            <polyline points="6 9 12 15 18 9" />
                          </svg>
                        </div>

                        {/* expanded detail panel */}
                        {isExpanded && (
                          <div
                            style={{
                              padding: "0 18px 14px 70px",
                              animation: "expandIn 0.18s ease",
                            }}
                          >
                            <div
                              style={{
                                background: "#f8fafc",
                                borderRadius: 10,
                                border: "1px solid #e5e7eb",
                                overflow: "hidden",
                              }}
                            >
                              {/* detail header */}
                              <div
                                style={{
                                  padding: "10px 14px 8px",
                                  borderBottom: "1px solid #f0f0f0",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 8,
                                }}
                              >
                                <span
                                  style={{
                                    fontSize: 11,
                                    fontWeight: 800,
                                    color: "#64748b",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.8px",
                                  }}
                                >
                                  Event Details
                                </span>
                                <span
                                  style={{
                                    fontSize: 10,
                                    color: "#94a3b8",
                                    fontFamily: "monospace",
                                  }}
                                >
                                  {log._id}
                                </span>
                              </div>

                              {/* key fields grid */}
                              <div
                                style={{
                                  display: "grid",
                                  gridTemplateColumns:
                                    "repeat(auto-fill, minmax(180px, 1fr))",
                                  gap: 1,
                                  background: "#e5e7eb",
                                }}
                              >
                                {[
                                  { label: "Action", value: cfg.label },
                                  { label: "User", value: phone },
                                  { label: "Role", value: log.userRole || "—" },
                                  {
                                    label: "Entity",
                                    value: log.entityType || "—",
                                  },
                                  {
                                    label: "Entity ID",
                                    value: log.entityId || "—",
                                    mono: true,
                                  },
                                  {
                                    label: "IP",
                                    value: log.ip || "—",
                                    mono: true,
                                  },
                                  {
                                    label: "Time",
                                    value: formatDate(
                                      log.createdAt || log.timestamp,
                                    ),
                                  },
                                ].map(({ label, value, mono }) => (
                                  <div
                                    key={label}
                                    style={{
                                      background: "#fff",
                                      padding: "8px 12px",
                                    }}
                                  >
                                    <p
                                      style={{
                                        fontSize: 9,
                                        fontWeight: 800,
                                        color: "#94a3b8",
                                        textTransform: "uppercase",
                                        letterSpacing: "0.8px",
                                        margin: "0 0 2px",
                                      }}
                                    >
                                      {label}
                                    </p>
                                    <p
                                      style={{
                                        fontSize: 12,
                                        fontWeight: 700,
                                        color: "#374151",
                                        margin: 0,
                                        fontFamily: mono
                                          ? "monospace"
                                          : "inherit",
                                        wordBreak: "break-all",
                                      }}
                                    >
                                      {value}
                                    </p>
                                  </div>
                                ))}
                              </div>

                              {/* raw details JSON */}
                              {log.details &&
                                Object.keys(log.details).length > 0 && (
                                  <div style={{ padding: "10px 14px" }}>
                                    <p
                                      style={{
                                        fontSize: 9,
                                        fontWeight: 800,
                                        color: "#94a3b8",
                                        textTransform: "uppercase",
                                        letterSpacing: "0.8px",
                                        margin: "0 0 6px",
                                      }}
                                    >
                                      Payload
                                    </p>
                                    <pre
                                      style={{
                                        fontSize: 11.5,
                                        color: "#374151",
                                        margin: 0,
                                        fontFamily: "monospace",
                                        lineHeight: 1.7,
                                        whiteSpace: "pre-wrap",
                                        wordBreak: "break-all",
                                      }}
                                    >
                                      {JSON.stringify(log.details, null, 2)}
                                    </pre>
                                  </div>
                                )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}

            {/* ── Pagination ── */}
            {totalPages > 1 && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 6,
                  marginTop: 24,
                  flexWrap: "wrap",
                }}
              >
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  style={{
                    padding: "8px 14px",
                    borderRadius: 9,
                    border: "1.5px solid #e5e7eb",
                    background: "#fff",
                    color: page === 1 ? "#d1d5db" : "#374151",
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: page === 1 ? "not-allowed" : "pointer",
                    transition: "all 0.15s",
                  }}
                  onMouseEnter={(e) => {
                    if (page > 1) e.currentTarget.style.borderColor = "#2D8B4E";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "#e5e7eb";
                  }}
                >
                  ← Prev
                </button>

                {Array.from({ length: Math.min(7, totalPages) }, (_, i) => {
                  let p;
                  if (totalPages <= 7) {
                    p = i + 1;
                  } else if (page <= 4) {
                    p = i + 1;
                  } else if (page >= totalPages - 3) {
                    p = totalPages - 6 + i;
                  } else {
                    p = page - 3 + i;
                  }
                  const isActive = p === page;
                  return (
                    <button
                      key={p}
                      onClick={() => setPage(p)}
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: 9,
                        border: `1.5px solid ${isActive ? "#2D8B4E" : "#e5e7eb"}`,
                        background: isActive
                          ? "linear-gradient(135deg, #2D8B4E, #4CAF72)"
                          : "#fff",
                        color: isActive ? "#fff" : "#374151",
                        fontSize: 13,
                        fontWeight: isActive ? 800 : 500,
                        cursor: "pointer",
                        boxShadow: isActive
                          ? "0 2px 10px rgba(45,139,78,0.3)"
                          : "none",
                        transition: "all 0.15s",
                      }}
                    >
                      {p}
                    </button>
                  );
                })}

                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  style={{
                    padding: "8px 14px",
                    borderRadius: 9,
                    border: "1.5px solid #e5e7eb",
                    background: "#fff",
                    color: page === totalPages ? "#d1d5db" : "#374151",
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: page === totalPages ? "not-allowed" : "pointer",
                    transition: "all 0.15s",
                  }}
                  onMouseEnter={(e) => {
                    if (page < totalPages)
                      e.currentTarget.style.borderColor = "#2D8B4E";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "#e5e7eb";
                  }}
                >
                  Next →
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <style>{`
        @keyframes spin      { to { transform: rotate(360deg); } }
        @keyframes dropIn    { from { opacity:0; transform:translateY(-6px) scale(0.98); } to { opacity:1; transform:translateY(0) scale(1); } }
        @keyframes expandIn  { from { opacity:0; transform:translateY(-4px); } to { opacity:1; transform:translateY(0); } }
      `}</style>
    </div>
  );
}

/* ─── demo data ─── */
const DEMO_LOGS = [
  {
    _id: "d1",
    action: "order_status",
    userPhone: "+919945086069",
    userRole: "admin",
    entityType: "Order",
    entityId: "FRH89503398",
    details: { from: "preparing", to: "shipped" },
    createdAt: new Date().toISOString(),
  },
  {
    _id: "d2",
    action: "product_updated",
    userPhone: "+919945086069",
    userRole: "admin",
    entityType: "Product",
    entityId: "abc123456789",
    details: { name: "Mango Burst", price: 89, stock: 45 },
    createdAt: new Date().toISOString(),
  },
  {
    _id: "d3",
    action: "order_status",
    userPhone: "+919945086069",
    userRole: "admin",
    entityType: "Order",
    entityId: "FRH89503123",
    details: { from: "confirmed", to: "preparing" },
    createdAt: new Date(Date.now() - 3600000).toISOString(),
  },
  {
    _id: "d4",
    action: "login",
    userPhone: "+919945086069",
    userRole: "admin",
    entityType: "Auth",
    ip: "127.0.0.1",
    details: {},
    createdAt: new Date(Date.now() - 7200000).toISOString(),
  },
  {
    _id: "d5",
    action: "order_created",
    userPhone: "+919876543210",
    userRole: "user",
    entityType: "Order",
    entityId: "FRH89503001",
    details: {},
    createdAt: new Date(Date.now() - 86400000).toISOString(),
  },
  {
    _id: "d6",
    action: "otp_verified",
    userPhone: "+919876543210",
    userRole: "user",
    entityType: "Auth",
    details: {},
    createdAt: new Date(Date.now() - 86500000).toISOString(),
  },
  {
    _id: "d7",
    action: "product_added",
    userPhone: "+919945086069",
    userRole: "admin",
    entityType: "Product",
    entityId: "xyz987654321",
    details: { name: "Berry Blast", price: 99 },
    createdAt: new Date(Date.now() - 172800000).toISOString(),
  },
  {
    _id: "d8",
    action: "role_changed",
    userPhone: "+919945086069",
    userRole: "admin",
    entityType: "User",
    entityId: "usr123",
    details: { newRole: "admin" },
    createdAt: new Date(Date.now() - 172900000).toISOString(),
  },
];
